import { Table } from "reactstrap";

/**
 * DataTable — shared list table with token-based header, loading skeleton
 * and empty state.
 *
 * Props:
 * - columns:      [{ key, label, render?, className? }] — header + default cell
 *                 rendering. render(row, rowIndex) overrides row[key].
 * - data:         array of rows
 * - isLoading:    show skeleton rows instead of data
 * - emptyMessage: shown when !isLoading && data.length === 0
 * - renderRow:    optional (row, rowIndex) => <tr> for complex pages;
 *                 columns still drive the header
 * - keyField:     row property used as React key (default "id", falls back
 *                 to the row index)
 * - skeletonRows: number of placeholder rows while loading (default 5)
 * - className:    extra classes on the <Table>
 * - footer:       optional <tr>…</tr> (or fragment) rendered inside <tfoot>
 *                 when not loading (report totals, etc.)
 */
const DataTable = ({
  columns = [],
  data = [],
  isLoading = false,
  emptyMessage = "No records found",
  renderRow,
  keyField = "id",
  skeletonRows = 5,
  className = "",
  footer = null,
}) => {
  const colCount = columns.length || 1;

  const renderSkeleton = () =>
    Array.from({ length: skeletonRows }).map((_, rowIndex) => (
      <tr key={`skeleton-${rowIndex}`} aria-hidden="true">
        {Array.from({ length: colCount }).map((_, cellIndex) => (
          <td key={cellIndex}>
            <span className="akh-skeleton" />
          </td>
        ))}
      </tr>
    ));

  const renderEmpty = () => (
    <tr>
      <td colSpan={colCount}>
        <div className="akh-empty-state">
          <i className="fas fa-inbox" aria-hidden="true" />
          <span>{emptyMessage}</span>
        </div>
      </td>
    </tr>
  );

  const renderRows = () =>
    data.map((row, rowIndex) => {
      if (renderRow) return renderRow(row, rowIndex);
      const key = row?.[keyField] !== undefined ? row[keyField] : rowIndex;
      return (
        <tr key={key}>
          {columns.map((column) => (
            <td key={column.key} className={column.className}>
              {column.render ? column.render(row, rowIndex) : row?.[column.key]}
            </td>
          ))}
        </tr>
      );
    });

  return (
    <Table
      className={`akh-data-table align-items-center table-flush table-head-accent ${className}`.trim()}
      responsive
    >
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.key} scope="col" className={column.className}>
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading
          ? renderSkeleton()
          : data.length === 0
          ? renderEmpty()
          : renderRows()}
      </tbody>
      {!isLoading && footer ? <tfoot>{footer}</tfoot> : null}
    </Table>
  );
};

export default DataTable;
